import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import {
  listCohortSubmissions,
  type CohortSubmission,
  type MetricStat,
} from '../api/cohortBaseline';
import { useSortedRows } from '../util/tableSort';
import { SortHeader } from './SortHeader';

const NO_METRICS: MetricStat[] = [];

const METRIC_ACCESSORS = {
  metric: (m: MetricStat) => m.metric,
  median: (m: MetricStat) => m.median,
  mean: (m: MetricStat) => m.mean,
  n: (m: MetricStat) => m.n,
};

/**
 * Provisional pending-baseline overlay (master-baseline-cohort-submit #3).
 *
 * After a tenant admin submits a cohort baseline (Cohort baseline page) it
 * sits in the superadmin review queue — until approved it does NOT drive
 * the Faculty comparison bars. This card surfaces the tenant's own pending
 * submission for the selected procedure on the Dashboard, clearly labelled
 * as provisional / not-yet-approved, so they can eyeball their participants
 * against it early. Reuses the tenant submissions endpoint (no dashboard
 * aggregation changes). Shows only the tenant's own pending data.
 */

function fmt(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (Number.isInteger(v)) return String(v);
  return Number(v.toFixed(3)).toString();
}

export function PendingBaselineCard({
  tenantId,
  procedure,
}: {
  tenantId: string;
  procedure: string;
}) {
  const [subs, setSubs] = useState<CohortSubmission[] | null>(null);

  useEffect(() => {
    let alive = true;
    listCohortSubmissions(tenantId)
      .then((rows) => {
        if (alive) setSubs(rows);
      })
      .catch(() => {
        if (alive) setSubs([]);
      });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  // Match the current procedure filter — a specific procedure, or the
  // all-procedures (global) submission when no filter is set. List is
  // newest-first, so the first match is the latest.
  const match = subs?.find(
    (s) =>
      s.status === 'pending' &&
      (procedure ? s.procedure_name === procedure : s.procedure_name == null),
  );
  const { sort, sorted } = useSortedRows(
    match?.metrics ?? NO_METRICS,
    METRIC_ACCESSORS,
    'metric',
  );
  if (!subs || !match) return null;

  return (
    <Alert severity="warning" icon={<HourglassEmptyIcon />}>
      <AlertTitle>Pending baseline — in review</AlertTitle>
      <Stack spacing={1}>
        <Typography variant="body2">
          You submitted a cohort baseline for{' '}
          <strong>{match.procedure_name ?? 'all procedures'}</strong> (cohort{' '}
          <Chip label={match.cohort} size="small" />, {match.session_count}{' '}
          sessions). It's <strong>not yet approved</strong>, so these values
          are provisional and don't drive the Faculty bars below yet.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader label="Metric" col="metric" sort={sort} />
              <SortHeader label="Median" col="median" sort={sort} align="right" />
              <SortHeader label="Mean" col="mean" sort={sort} align="right" />
              <SortHeader label="N" col="n" sort={sort} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((m) => (
              <TableRow key={m.metric}>
                <TableCell>{m.metric}</TableCell>
                <TableCell align="right">{fmt(m.median)}</TableCell>
                <TableCell align="right">{fmt(m.mean)}</TableCell>
                <TableCell align="right">{m.n}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Alert>
  );
}
